import { CaseLawGavel, InfoCircle } from '@/assets/icons'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import * as Switch from '@radix-ui/react-switch'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MenuContent, MenuSeparator } from '@/components/menu/MenuContent'
import { SelectButton } from '@/components/select-button/SelectButton'
import { useEffect, useRef, useState } from 'react'
import SourceDialog from '../sources-dialog/SourceDialog'
import { selectAgentConversationFormResearchEnabled, selectAgentConversationFormWebSearchEnabled } from '@/agent-v2/chat-form/store/selectors'
import { selectResearchFiltersDisplay, selectResearchFiltersForConversation } from '../sources-dialog/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { RiaToggleState, RiaToggleName, RiaDialogSource } from '@/analytics/enums/ria.enums'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { useJurisdictionPrefill } from '../hooks/useJurisdictionPrefill'

type SourcesControlsProps = {
  conversationId: string
  /** The conversation's matter — each consumer supplies its own sourcing (see useJurisdictionPrefill). */
  matterId: string | null
  /** True once persisted filters have been restored, or the consumer has none to restore. */
  prefillReady: boolean
  /** Trigger pill label. */
  triggerLabel: string
  /** Disables the trigger, e.g. while the conversation it would store selections against isn't resolved yet. */
  disabled?: boolean
  /** Collapses the trigger to icon-only when the composer is narrow. Requires an ancestor with container-type: inline-size. */
  responsiveLabel?: boolean
  /** Web search is only available in Agent V2. */
  showWebSearch?: boolean
}

// Small gap between the trigger chip and the menu. Radix flips/shifts the menu itself when there
// isn't room on the preferred side, so this no longer has to clear the textarea by brute force.
const SOURCES_DROPDOWN_OFFSET_PX = 8

// Matches the 20px-per-side gutter the menu's `calc(100vw-40px)` width is written for, so a
// collision-shifted menu stops short of the screen edge instead of sitting flush against it.
const SOURCES_DROPDOWN_VIEWPORT_GUTTER_PX = 20

const kWebSearchInfo = 'Search the public web for news, articles, and other sources beyond case law and statutes.'

function WebSearchInfoButton() {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: PointerEvent) => {
      if (buttonRef.current?.contains(event.target as Node)) return
      setIsOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  return (
    <CustomizableTooltip
      open={isOpen}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      placement="top"
      title={kWebSearchInfo}
      tooltipClassName="block max-w-60 whitespace-normal px-2.5 py-1.5 text-left text-2xs font-medium leading-4 !bg-brand-neutral-800 !text-white"
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label="About web search"
        aria-expanded={isOpen}
        className="flex text-brand-neutral-500 outline-hidden hover:text-brand-neutral-700 focus-visible:outline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setIsOpen((open) => !open)
        }}
      >
        <InfoCircle className="size-3.5" aria-hidden />
      </button>
    </CustomizableTooltip>
  )
}

function SourcesControls(props: SourcesControlsProps) {
  const { conversationId, matterId, prefillReady, triggerLabel, disabled = false, responsiveLabel = false, showWebSearch = false } = props
  const dispatch = useAppDispatch()
  const [isSourceDialogOpen, setIsSourceDialogOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { trackEvent } = useAnalytics()

  const isResearchEnabled = useAppSelector((state: RootState) => selectAgentConversationFormResearchEnabled(state, { conversationId }))
  const isWebSearchEnabled = useAppSelector((state: RootState) => selectAgentConversationFormWebSearchEnabled(state, { conversationId }))

  const prefillDefaultResearchSources = useJurisdictionPrefill({ conversationId, matterId, prefillReady })

  // Select description for selected sources from filters UI state
  const selectedSourcesDescription = useAppSelector((state: RootState) => selectResearchFiltersDisplay(state, { conversationId }))

  // Select research filters for restoring selections when toggling ON
  const researchFilters = useAppSelector((state: RootState) => selectResearchFiltersForConversation(state, { conversationId }))

  const handleSourceDialogOpenChange = (open: boolean) => {
    if (open) {
      prefillDefaultResearchSources()
    }

    setIsSourceDialogOpen(open)
    if (open) {
      setIsDropdownOpen(false)
      trackEvent(AnalyticsEvent.RiaSourcesDialogOpened, {
        riaDialogSource: RiaDialogSource.KnowledgeSourcesDropdown,
        conversationId,
      })
    }
  }

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newState = isResearchEnabled ? RiaToggleState.Off : RiaToggleState.On

    trackEvent(AnalyticsEvent.RiaToggleClicked, {
      riaToggleState: newState,
      riaToggleName: RiaToggleName.LegalResearch,
      conversationId,
    })

    if (newState === RiaToggleState.Off) {
      dispatch(AgentConversationFormActions.clearResearchJurisdictions({ conversationId }))
    } else {
      if (researchFilters) {
        dispatch(
          AgentConversationFormActions.syncResearchFiltersToRequestParams({
            conversationId,
            stateLrrSources: researchFilters.stateLrrSources,
            stateCaselawSources: researchFilters.stateCaselawSources,
            federalLrrSources: researchFilters.federalLrrSources,
            federalCaselawSources: researchFilters.federalCaselawSources,
          })
        )
      }
      handleSourceDialogOpenChange(true)
    }
  }

  const handleWebSearchToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(AgentConversationFormActions.setWebSearchEnabled({ conversationId, enabled: !isWebSearchEnabled }))
  }

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open)
    if (open) {
      trackEvent(AnalyticsEvent.RiaSourcesButtonClicked, {
        conversationId,
      })
    }
  }

  return (
    <>
      <SourceDialog open={isSourceDialogOpen} onOpenChange={handleSourceDialogOpenChange} conversationId={conversationId} />
      <DropdownMenu.Root open={isDropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <DropdownMenu.Trigger asChild disabled={disabled}>
          <SelectButton
            size="sm"
            color={isResearchEnabled || (showWebSearch && isWebSearchEnabled) ? 'blue' : 'neutral'}
            leadingIcon={CaseLawGavel}
            aria-label="Open sources DropdownMenu"
            disabled={disabled}
            hideLabelBelow={responsiveLabel ? 400 : undefined}
          >
            {triggerLabel}
          </SelectButton>
        </DropdownMenu.Trigger>
        <MenuContent
          className="w-[calc(100vw-40px)] max-w-[348px] border-0"
          side="top"
          sideOffset={SOURCES_DROPDOWN_OFFSET_PX}
          align="start"
          collisionPadding={SOURCES_DROPDOWN_VIEWPORT_GUTTER_PX}
        >
          <DropdownMenu.Label className="px-4 py-2 text-sm font-medium text-brand-neutral-500 bg-brand-neutral-50">Knowledge Sources</DropdownMenu.Label>
          <MenuSeparator />
          <div className="m-2 hover:bg-brand-neutral-100 rounded cursor-pointer transition-colors" onClick={handleToggleClick}>
            <div className="flex items-center justify-between gap-2 text-brand-neutral-700 text-sm font-medium px-2 pt-2 pb-1">
              <div className="flex items-center gap-2">
                <span>Legal Research</span>
              </div>
              <div className="flex items-center" onClick={handleToggleClick}>
                <Switch.Root
                  className="relative h-[25px] w-[42px] cursor-default rounded-full bg-brand-neutral-400 outline-hidden data-[state=checked]:bg-brand-500 transition-all duration-300"
                  id="legal-research-switch"
                  checked={isResearchEnabled}
                >
                  <Switch.Thumb className="block size-[21px] translate-x-0.5 rounded-full bg-white transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[19px]" />
                </Switch.Root>
              </div>
            </div>
            {isResearchEnabled ? (
              <div className="flex items-center justify-start gap-2 text-brand-neutral-600 text-xs font-medium pt-0 pb-2 px-2">
                <span>{selectedSourcesDescription}</span>
                <button
                  type="button"
                  className="text-brand-500 text-xs font-medium underline"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSourceDialogOpenChange(true)
                  }}
                >
                  Modify
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2 text-brand-neutral-600 text-xs font-medium pt-0 pb-2 px-2">
                <span>Case law, statutory, and regulatory authority</span>
              </div>
            )}
          </div>
          {showWebSearch && (
            <>
              <MenuSeparator />
              <div className="m-2 hover:bg-brand-neutral-100 rounded cursor-pointer transition-colors" onClick={handleWebSearchToggleClick}>
                <div className="flex items-center justify-between gap-2 text-brand-neutral-700 text-sm font-medium px-2 py-2">
                  <span className="flex items-center gap-1.5">
                    Web search
                    <WebSearchInfoButton />
                  </span>
                  <div className="flex items-center" onClick={handleWebSearchToggleClick}>
                    <Switch.Root
                      className="relative h-[25px] w-[42px] cursor-default rounded-full bg-brand-neutral-400 outline-hidden data-[state=checked]:bg-brand-500 transition-all duration-300"
                      id={`web-search-switch-${conversationId}`}
                      checked={isWebSearchEnabled}
                      aria-label="Web search"
                    >
                      <Switch.Thumb className="block size-[21px] translate-x-0.5 rounded-full bg-white transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[19px]" />
                    </Switch.Root>
                  </div>
                </div>
              </div>
            </>
          )}
        </MenuContent>
      </DropdownMenu.Root>
    </>
  )
}

export default SourcesControls
